import style from './CardSettingsPage.css';
import useTranslation from '../../localization/i18n.context';
import List from '../../components/shared/List/List';
import ListItem from '../../components/shared/List/ListItem';
import ErrorTriangle from '../../assets/images/info-triangle.webp';
import LockIcon from '../../components/shared/Icons/LockIcon';
import PasswordIcon from '../../components/shared/Icons/PasswordIcon';
import RenewIcon from '../../components/shared/Icons/RenewIcon';
import Popup from '../../components/shared/Popup/Popup';
import { useEffect, useState } from 'preact/hooks';
import Toast from '../../components/shared/Toast/Toast';
import NavigationUtils from '../../utils/navigation.utils';
import CardStatus from '../../constants/card.status';
import CardService from '../../services/card.service';
import { TrackingEvent } from '../../sdk/tracking.event';

const PopupTypes = {
  CONFIRM_LOCK: 'confirm-lock',
  REQUEST_CARD: 'request-card',
  NO_BALANCE: 'no-balance',
};

const hidePopup = cardUid => {
  NavigationUtils.navigate(NavigationUtils.Routes.CARD_SETTINGS, true, {
    cardUid,
  });
};

export default function CardSettingsView({
  popupType = null,
  settings,
  organization,
}) {
  const { t, getCurrency } = useTranslation();

  const [isLocked, setIsLocked] = useState(
    settings.status === CardStatus.LOCKED,
  );

  const [error, setError] = useState(null);

  const updateStatus = () => {
    const action = !isLocked ? 'lock' : 'unlock';

    CardService.updateStatus(organization, action, settings.cardUid)
      .then(() => {
        const eventName = isLocked
          ? TrackingEvent.Events.LOCK_CARD_FALSE
          : TrackingEvent.Events.LOCK_CARD_TRUE;

        setIsLocked(!isLocked);
        TrackingEvent.trigger(eventName, TrackingEvent.Sources.CAAS);
      })
      .catch(() => {
        setError(t('Something went wrong. Please try again'));
      });
  };

  const requestNewCard = () => {
    CardService.requestNewCard(organization, settings.cardUid)
      .then(() => {
        TrackingEvent.trigger(
          TrackingEvent.Events.REPORT_CARD_REPRINT_TAP,
          TrackingEvent.Sources.CAAS,
        );
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.REPORT_CARD_STATUS}/success`,
        );
      })
      .catch(() => {
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.REPORT_CARD_STATUS}/error`,
        );
      });
  };

  // hide the toast
  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, Toast.Delay);
    }
  }, [error]);

  const PopupContent = {
    [PopupTypes.CONFIRM_LOCK]: {
      title: t('Are you sure you want to lock your card?'),
      subtitle: t(
        "By locking your card, any card payment, transaction and ATM withdrawal won't work. You can unlock your card instantly anytime",
      ),
      primaryCta: {
        text: t('Lock card'),
        onClick: () => {
          updateStatus();
          hidePopup(settings.cardUid);
        },
      },
      secondaryCta: {
        text: t('Cancel'),
        onClick: () => {
          hidePopup(settings.cardUid);
        },
      },
    },

    [PopupTypes.REQUEST_CARD]: {
      title: t('Are you sure you want to get a new card?'),
      subtitle: t(
        'This will immediately lock your current card permanently and it will no longer work. You will automatically pay {currency} {charges} for your new card',
        {
          '{currency}': getCurrency(settings.currency),
          '{charges}': settings.minNewCardBalance,
        },
      ),
      primaryCta: {
        text: t('Yes, get a new card'),
        onClick: () => {
          requestNewCard();
        },
      },
      secondaryCta: {
        text: t('No, cancel'),
        onClick: () => {
          hidePopup(settings.cardUid);
        },
      },
    },

    [PopupTypes.NO_BALANCE]: {
      title: t("You don't have enough balance to get a new card"),
      subtitle: t(
        'You need to have {currency} {charges} in your balance to replace your card. Top up your card and try again',
        {
          '{currency}': getCurrency(settings.currency),
          '{charges}': settings.minNewCardBalance,
        },
      ),
      primaryCta: {
        text: t('Close'),
        onClick: () => {
          hidePopup(settings.cardUid);
        },
      },
    },
  };
  const popupData =
    PopupContent[popupType] ?? PopupContent[PopupTypes.CONFIRM_LOCK];

  return (
    <main className={style.main}>
      <List>
        <ListItem
          title={t('Lock card')}
          subtitle={t('Block all transactions')}
          icon={<LockIcon />}
          actionable
          size={ListItem.Size.LARGE}
          type={ListItem.Type.SWITCH}
          actionProps={{
            checked: isLocked,
            onChange: () => {
              if (!settings.activationDate) {
                return setError(
                  t('Card is not activated, cannot be locked yet'),
                );
              }

              if (!isLocked && !settings.lockDate) {
                NavigationUtils.navigate(
                  NavigationUtils.Routes.CARD_SETTINGS,
                  false,
                  { popup: PopupTypes.CONFIRM_LOCK, cardUid: settings.cardUid },
                );
                return;
              }
              updateStatus();
            },
          }}
        />
        <ListItem
          title={t('Reset PIN')}
          icon={<PasswordIcon />}
          actionable
          size={ListItem.Size.LARGE}
          onClick={() => {
            if (!settings.activationDate) {
              return setError(
                t('Your card is not active, cannot reset the PIN yet'),
              );
            }

            TrackingEvent.trigger(
              TrackingEvent.Events.RESET_PIN_TAP,
              TrackingEvent.Sources.CAAS,
            );
            NavigationUtils.navigate(NavigationUtils.Routes.RESET_PIN, false, {
              cardUid: settings.cardUid,
            });
          }}
        />
        <ListItem
          title={t('Get a new card')}
          subtitle={t('Lost, stolen or broken')}
          icon={<RenewIcon />}
          actionable
          size={ListItem.Size.LARGE}
          onClick={() => {
            TrackingEvent.trigger(
              TrackingEvent.Events.REPORT_CARD_ENTRY_POINT_TAP,
              TrackingEvent.Sources.CAAS,
              {
                enough_balance: settings.balance >= settings.minNewCardBalance,
              },
            );

            if (!settings.activationDate) {
              TrackingEvent.trigger(
                TrackingEvent.Events.REPORT_CARD_NOT_ACTIVE_SHOW,
                TrackingEvent.Sources.CAAS,
              );
              return setError(t('Your card is not active yet'));
            }

            if (settings.balance >= settings.minNewCardBalance) {
              TrackingEvent.trigger(
                TrackingEvent.Events.REPORT_CARD_BALANCE_ENOUGH_SHOW,
                TrackingEvent.Sources.CAAS,
              );

              NavigationUtils.navigate(
                NavigationUtils.Routes.CARD_SETTINGS,
                false,
                {
                  popup: PopupTypes.REQUEST_CARD,
                  cardUid: settings.cardUid,
                },
              );
            } else {
              TrackingEvent.trigger(
                TrackingEvent.Events.REPORT_CARD_BALANCE_NOT_ENOUGH_SHOW,
                TrackingEvent.Sources.CAAS,
              );
              NavigationUtils.navigate(
                NavigationUtils.Routes.CARD_SETTINGS,
                false,
                {
                  popup: PopupTypes.NO_BALANCE,
                  cardUid: settings.cardUid,
                },
              );
            }
          }}
        />
      </List>
      <Popup
        title={popupData.title}
        subtitle={popupData.subtitle}
        icon={ErrorTriangle}
        showing={!!popupType}
        primaryCta={popupData.primaryCta}
        secondaryCta={popupData.secondaryCta}
        onCloseClick={() => hidePopup(settings.cardUid)}
        data-testid="card-settings-pop-up"
      />
      <Toast
        message={error}
        type={Toast.Type.ERROR}
        floating
        autoDismiss
        showing={!!error}
        onHide={() => setError(null)}
      />
    </main>
  );
}
